import {
  alfenSCNChart,
  alfenSocketChart,
  alpitronicHyperchargerChart,
  autelChargerChart,
  defaChargerChart,
  dlmsCosemChart,
  dsmr5Chart,
  easeeCircuitChart,
  nexBlueChart,
  modbusRTUMeterChart,
  modbusRTUMeterZEVChart,
  modbusTCPMeterChart,
  rpiGpio,
  SGrSubMeterElectricityChart,
  shellyProMeterChart,
  smartPiCharts,
  supernovaChart,
  kempowerChart,
  technagonACChart,
  zaptecInstallationChart,
} from '../constants/chartDefinitions';
import {metricDisplayNames, NON_CHARTABLE_CODECS} from '../constants/chartMetrics';

/**
 * Mapping of EV charger types to their chart generators
 */
export const evChargerMapping = {
  easeeCircuit: (evCharger) => ({
    name: `Easee Circuit, SiteId: ${evCharger.easeeCircuit.siteId}, CircuitId: ${evCharger.easeeCircuit.circuitId}`,
    charts: easeeCircuitChart(evCharger.easeeCircuit.siteId, evCharger.easeeCircuit.circuitId),
  }),
  zaptecInstallation: (evCharger) => ({
    name: `Zaptec Installation, installationId: ${evCharger.zaptecInstallation.installationId}`,
    charts: zaptecInstallationChart(evCharger.zaptecInstallation.installationId),
  }),
  alfenSCN: (evCharger) => ({
    name: `Alfen SCN, IP: ${evCharger.alfenSCN.ip}`,
    charts: alfenSCNChart(evCharger.alfenSCN.ip),
  }),
  alfenSocket: (evCharger) => ({
    name: `Alfen Socket, IP: ${evCharger.alfenSocket.ip}, SocketId: ${evCharger.alfenSocket.socketId}`,
    charts: alfenSocketChart(evCharger.alfenSocket.ip, evCharger.alfenSocket.socketId),
  }),
  autel: (evCharger) => ({
    name: `Autel, IP: ${evCharger.autel.ip}, SocketId: ${evCharger.autel.socketId}`,
    charts: autelChargerChart(evCharger.autel.ip, evCharger.autel.socketId),
  }),
  defa: (evCharger) => ({
    name: `DEFA Power, IP: ${evCharger.defa.ip}`,
    charts: defaChargerChart(evCharger.defa.ip),
  }),
  nexBlue: (evCharger) => ({
    name: `NexBlue, IP: ${evCharger.nexBlue.ip}`,
    charts: nexBlueChart(evCharger.nexBlue.ip),
  }),
  technagonAC: (evCharger) => ({
    name: `Technagon AC, IP: ${evCharger.technagonAC.ip}, Charging Point Id: ${evCharger.technagonAC.socketId}`,
    charts: technagonACChart(evCharger.technagonAC.ip, evCharger.technagonAC.socketId),
  }),
  alpitronicHypercharger: (evCharger) => ({
    name: `Alpitronic HYC, IP: ${evCharger.alpitronicHypercharger.ip}`,
    charts: alpitronicHyperchargerChart(evCharger.alpitronicHypercharger.ip),
  }),
  supernova: (evCharger) => ({
    name: `Supernova, IP: ${evCharger.supernova.ip}`,
    charts: supernovaChart(evCharger.supernova.ip),
  }),
  kempower: (evCharger) => ({
    name: `Kempower, IP: ${evCharger.kempower.ip}`,
    charts: kempowerChart(evCharger.kempower.ip),
  }),
};

/**
 * Extract EV chargers from a circuit recursively
 */
export const findEVsInCircuit = (circuit) => {
  const res = circuit?.evChargers?.map((evCharger) => {
    for (const key in evChargerMapping) {
      if (evCharger[key]) {
        return {
          chargerType: key,
          ...evChargerMapping[key](evCharger),
        };
      }
    }
    return null;
  }).filter(Boolean) || [];

  // Recursively process nested circuits
  if (circuit.circuits) {
    circuit.circuits.forEach((c) => {
      res.push(...findEVsInCircuit(c));
    });
  }

  return res;
};

/**
 * Build dynamic charts for generic Modbus meters based on template
 */
export const buildDynamicModbusMeterCharts = (deviceName, template) => {
  if (!template?.datapoints) return [];

  const charts = [];
  const datapoints = template.datapoints;

  const containsChartableData = (node) => {
    if (node.codec) {
      return !NON_CHARTABLE_CODECS.includes(node.codec);
    }
    if (typeof node === "object") {
      return Object.values(node).some(containsChartableData);
    }
    return false;
  };

  for (const [metricKey, metricConfig] of Object.entries(datapoints)) {
    if (!containsChartableData(metricConfig)) continue;

    charts.push({
      filters: [
        {name: "tags_1", value: ["devices"]},
        {name: "tags_2", value: ["ModbusMeter"]},
        {name: "tags_3", value: [deviceName]},
        {name: "tags_4", value: [metricKey]},
      ],
      name: metricDisplayNames[metricKey] || metricKey.toUpperCase(),
      type: "TimeSeriesLineChart",
    });
  }

  return charts;
};

/**
 * Build complete charts configuration from edge config
 * Groups charts by device and EV charger, returns structured config for UI
 */
export const buildChartsConfig = (conf, modbusTemplates) => {
  const chartsConf = {
    influxAPIUrl: "https://influxapi.smartchargecontroller.ch/v1?bucket=sccmetrics",
    nrOfDataPoints: 360,
    groups: [],
  };

  const addGroup = (name, charts) => {
    chartsConf.groups.push({name, charts});
  };

  const processDevices = (devices, chartMapping) => {
    devices?.forEach(device => {
      const chart = chartMapping(device);
      if (chart) addGroup(device.name, chart);
    });
  };

  // Smart Pi (built-in measurement)
  if (conf.devices.SmartPi) {
    addGroup(conf.devices.SmartPi.name, smartPiCharts);
  }

  // Modbus RTU Meters
  processDevices(conf.devices.ModbusRTUMeter, m =>
    (m.deviceType === "PRO380-Mod-ZEV" || m.deviceType === "OptecMPro")
      ? modbusRTUMeterZEVChart(m.name)
      : modbusRTUMeterChart(m.name)
  );

  // Modbus TCP Meters
  processDevices(conf.devices.ModbusTCPMeter, m => modbusTCPMeterChart(m.name));

  // DSMR5 Smart Meters
  processDevices(conf.devices.DSMR5, m => dsmr5Chart(m.name));

  // DLMS/COSEM Meters
  processDevices(conf.devices.DlmsCosem, m => dlmsCosemChart(m.name, m.extendedData));

  // RPI GPIO
  processDevices(conf.devices.RpiGPIO, r => rpiGpio);

  // Shelly Pro 3EM Meters
  processDevices(conf.devices.ShellyPro3EM, m => shellyProMeterChart(m.name));

  // SGr Sub-Meter Electricity
  processDevices(conf.devices.SGrDevice, m =>
    m.category === "SubMeterElectricity"
      ? SGrSubMeterElectricityChart(m.name)
      : null
  );

  // Generic Modbus Meter
  processDevices(conf.devices.ModbusMeter, m => {
    const template = modbusTemplates[m.templateId];
    if (!template) {
      console.warn(`Template ${m.templateId} not found for device ${m.name}`);
      return [];
    }
    return buildDynamicModbusMeterCharts(m.name, template);
  });

  // EV Chargers from circuits
  conf.circuits?.forEach(circuit => {
    chartsConf.groups.push(...findEVsInCircuit(circuit));
  });

  return chartsConf;
};

